import React from 'react';
import { Activity, PersonStanding, TrendingDown, TrendingUp, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, YAxis, Tooltip, CartesianGrid } from 'recharts';

const extractVal = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'object') return val.value ?? val.val ?? val.current ?? null;
  return val;
};

export default function BiomechanicsCard({ biomechanicsData, sourceLabel }) {
  if (!biomechanicsData) return null;

  const { asymmetry, groundContactTime, verticalOscillation, history } = biomechanicsData;

  const asymmetryVal = extractVal(asymmetry?.current);
  const groundContactVal = extractVal(groundContactTime?.current);
  const groundContactAvg = extractVal(groundContactTime?.average);
  const verticalOscVal = extractVal(verticalOscillation?.current);

  // Asymmetry interpretation
  const asymmetryIsGood = asymmetryVal != null ? asymmetryVal <= 2.5 : true;
  const asymmetryColor = asymmetryVal == null ? 'var(--text-secondary)' : (asymmetryIsGood ? 'var(--health-green)' : '#ef4444');

  return (
    <div className="glass-panel" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="text-muted text-sm text-gradient-health" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
          <PersonStanding size={16} /> Biomecánica y Eficiencia
        </h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.65rem', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
            Próximamente
          </span>
          <span style={{ fontSize: '0.65rem', background: 'rgba(16,185,129,0.15)', color: 'var(--health-green)', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
            {sourceLabel || 'Sin sincronizar'}
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
        
        {/* ASIMETRÍA DE MARCHA */}
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '12px', border: `1px solid ${asymmetryIsGood ? 'rgba(255,255,255,0.05)' : 'rgba(239,68,68,0.2)'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
              <AlertTriangle size={12} color={asymmetryColor} /> Asimetría
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
            <span style={{ fontSize: '2rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>{asymmetryVal != null ? Number(asymmetryVal).toFixed(1) : '--'}</span>
            <span style={{ fontSize: '0.85rem', color: asymmetryColor, fontWeight: 600 }}>%</span>
          </div>
          <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.5rem', fontWeight: 500 }}>
            {asymmetryIsGood ? 'Distribución equilibrada' : '⚠️ Riesgo de sobrecarga unilateral'}
          </div>
        </div>

        {/* TIEMPO CONTACTO SUELO */}
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
              <Activity size={12} color="#f59e0b" /> Contacto Suelo
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
            <span style={{ fontSize: '2rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>{groundContactVal != null ? groundContactVal : '--'}</span>
            <span style={{ fontSize: '0.85rem', color: '#f59e0b', fontWeight: 600 }}>ms</span>
          </div>
          <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.5rem', fontWeight: 500 }}>
            Media últimos 7 días: {groundContactAvg != null ? groundContactAvg : '--'} ms
          </div>
        </div>

        {/* OSCILACIÓN VERTICAL */}
        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
              <TrendingUp size={12} color="#38bdf8" /> Oscilación Vert.
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
            <span style={{ fontSize: '2rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>{verticalOscVal != null ? Number(verticalOscVal).toFixed(1) : '--'}</span>
            <span style={{ fontSize: '0.85rem', color: '#38bdf8', fontWeight: 600 }}>cm</span>
          </div>
          <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.5rem', fontWeight: 500 }}>
            {verticalOscVal != null ? (verticalOscVal < 9 ? '🏃 Excelente economía' : 'Rebote elevado') : 'Sin datos recientes'}
          </div>
        </div>

      </div>

      {/* TENDENCIA ASIMETRIA */}
      {history && history.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <p className="text-xs text-muted" style={{ marginBottom: '0.5rem' }}>Evolución Asimetría (Últimos 14 días)</p>
          <div style={{ height: '50px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorAsym" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <YAxis domain={[0, 'dataMax + 2']} hide />
                <Tooltip contentStyle={{ background: '#111827', border: 'none', borderRadius: '8px', fontSize: '12px' }} />
                <Area type="monotone" dataKey="asymmetry" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorAsym)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
